import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useCalendarCalendars, useSyncCalendar, useToggleCalendar } from "@/hooks/useCalendar";
import { Calendar, Mail, RefreshCw, Loader2, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";
import { queryKeys } from "@/lib/query-keys";
import { GMAIL_CONFIG } from "@/lib/email/config";
import { outlookService } from "@/lib/email/outlook";
import { SenderDisconnectConfirmDialog } from "./SenderDisconnectConfirmDialog";
import {
  bulkPauseCampaigns,
  checkSenderDisconnectImpact,
  type AffectedCampaign,
} from "./_senderDisconnectChecks";

const MAX_ACCOUNTS = 5;

interface EmailProvider {
  id: string;
  type: string;
  email: string;
  workspace_id: string;
  last_calendar_sync_at?: string;
}

export function CalendarSettings() {
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const { data: calendars = [] } = useCalendarCalendars();
  const syncCalendar = useSyncCalendar();
  const toggleCalendar = useToggleCalendar();
  const queryClient = useQueryClient();
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  // Pre-flight check state — populated when the user clicks the trash icon.
  // disconnectTarget = the provider being disconnected (drives both dialogs).
  // affectedCampaigns = result of the pre-flight check (empty list → calendar-only dialog).
  const [disconnectTarget, setDisconnectTarget] = useState<EmailProvider | null>(null);
  const [affectedCampaigns, setAffectedCampaigns] = useState<AffectedCampaign[]>([]);
  const [checkingProviderId, setCheckingProviderId] = useState<string | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);

  // Fetch email providers (same tokens serve calendar)
  const { data: emailProviders = [], isLoading: providersLoading } = useQuery({
    queryKey: queryKeys.emailSettings.providers(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('email_providers')
        .select('id, type, email, workspace_id, last_calendar_sync_at')
        .eq('workspace_id', currentWorkspace.id);
      if (error) throw error;
      return data as EmailProvider[];
    },
    enabled: !!currentWorkspace?.id,
  });

  const isAtCap = emailProviders.length >= MAX_ACCOUNTS;

  const handleGoogleConnect = () => {
    if (!currentWorkspace?.id) return;
    if (isAtCap) {
      toast({
        title: "Account limit reached",
        description: `You can connect up to ${MAX_ACCOUNTS} accounts.`,
        variant: "destructive",
      });
      return;
    }

    localStorage.setItem('connecting_workspace_id', currentWorkspace.id);
    localStorage.setItem('connecting_provider', 'google_calendar');

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      new URLSearchParams({
        client_id: GMAIL_CONFIG.clientId,
        redirect_uri: GMAIL_CONFIG.redirectUri,
        response_type: 'code',
        scope: GMAIL_CONFIG.scope,
        access_type: 'offline',
        prompt: 'consent',
      }).toString();

    window.location.href = authUrl;
  };

  const handleOutlookConnect = async () => {
    if (!currentWorkspace?.id) return;
    if (isAtCap) {
      toast({
        title: "Account limit reached",
        description: `You can connect up to ${MAX_ACCOUNTS} accounts.`,
        variant: "destructive",
      });
      return;
    }

    localStorage.setItem('connecting_workspace_id', currentWorkspace.id);
    localStorage.setItem('connecting_provider', 'outlook_calendar');

    try {
      await outlookService.login();
    } catch (error) {
      console.error('Error connecting Outlook Calendar:', error);
      captureError(error as Error, { feature: 'calendar-settings-disconnect' });
      toast({
        title: "Error",
        description: "Failed to connect Outlook Calendar",
        variant: "destructive",
      });
    }
  };

  const handleSync = (providerId: string) => {
    syncCalendar.mutate(providerId);
  };

  // Step 1 of the disconnect flow — pre-flight impact check on the trash-icon
  // click. We open the confirm dialog regardless (the existing calendar-impact
  // dialog still applies even when no campaigns are affected) but the list of
  // affected Hermes campaigns determines which dialog variant we render.
  const handleDisconnectClick = async (provider: EmailProvider) => {
    if (!currentWorkspace?.id) return;

    setCheckingProviderId(provider.id);
    try {
      const { affectedCampaigns: affected } = await checkSenderDisconnectImpact({
        email: provider.email,
        workspaceId: currentWorkspace.id,
      });
      setAffectedCampaigns(affected);
      setDisconnectTarget(provider);
    } catch (error) {
      console.error('Error checking disconnect impact:', error);
      captureError(error as Error, { feature: 'sender-disconnect' });
      toast({
        title: "Error",
        description: "Failed to check campaigns for this account. Try again.",
        variant: "destructive",
      });
    } finally {
      setCheckingProviderId(null);
    }
  };

  // Step 2 — actually perform the disconnect. Same order as SenderSettings:
  //   1. Bulk-pause affected campaigns
  //   2. Delete email_providers row
  // (Calendar disconnect doesn't currently revoke MSAL because the email/Hermes
  // path may still be using the same Outlook session. If the row is deleted
  // here AND the user has no other Outlook providers, they'll be prompted to
  // re-auth on next Outlook send. This matches pre-change behavior.)
  const handleDisconnect = async () => {
    if (!disconnectTarget || isDisconnecting) return;

    setIsDisconnecting(true);
    try {
      if (affectedCampaigns.length > 0) {
        await bulkPauseCampaigns(affectedCampaigns.map((c) => c.id));
        queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      }

      const { error } = await supabase
        .from('email_providers')
        .delete()
        .eq('id', disconnectTarget.id);

      if (error) throw error;

      toast({
        description: affectedCampaigns.length > 0
          ? `Disconnected ${disconnectTarget.email} and paused ${affectedCampaigns.length} campaign${affectedCampaigns.length === 1 ? '' : 's'}.`
          : `Disconnected ${disconnectTarget.email}`,
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.providers(currentWorkspace?.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.calendarCalendars.all });
    } catch (error) {
      console.error('Error disconnecting account:', error);
      captureError(error as Error, { feature: 'sender-disconnect' });
      toast({
        title: "Error",
        description: "Failed to disconnect account",
        variant: "destructive",
      });
    } finally {
      setIsDisconnecting(false);
      setDisconnectTarget(null);
      setAffectedCampaigns([]);
    }
  };

  const toggleExpanded = (providerId: string) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      if (next.has(providerId)) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No workspace selected
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Calendar Integration</h2>
          <p className="text-muted-foreground">
            Connect your calendar accounts to sync events and meetings.
          </p>
        </div>

        <Popover open={addAccountOpen} onOpenChange={setAddAccountOpen}>
          <PopoverTrigger asChild>
            <Button disabled={isAtCap || providersLoading}>
              <Plus className="h-4 w-4 mr-2" />
              Add Account
              {isAtCap && <Badge variant="secondary" className="ml-2 text-xs">{MAX_ACCOUNTS}/{MAX_ACCOUNTS}</Badge>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="end">
            <button
              onClick={() => { setAddAccountOpen(false); handleGoogleConnect(); }}
              className="w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
            >
              <div className="h-4 w-4 flex items-center justify-center">
                <Calendar className="h-3.5 w-3.5 text-red-600" />
              </div>
              Google Account
            </button>
            <button
              onClick={() => { setAddAccountOpen(false); handleOutlookConnect(); }}
              className="w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
            >
              <div className="h-4 w-4 flex items-center justify-center">
                <Mail className="h-3.5 w-3.5 text-blue-600" />
              </div>
              Outlook Account
            </button>
          </PopoverContent>
        </Popover>
      </div>

      {providersLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : emailProviders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-1">No accounts connected</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Connect a Google or Outlook account to sync your calendars.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleGoogleConnect}>
                <Calendar className="h-4 w-4 mr-2 text-red-600" />
                Connect Google
              </Button>
              <Button variant="outline" onClick={handleOutlookConnect}>
                <Mail className="h-4 w-4 mr-2 text-blue-600" />
                Connect Outlook
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {emailProviders.map(provider => {
            const providerCalendars = calendars.filter(c => c.provider_id === provider.id);
            const isExpanded = expandedProviders.has(provider.id);
            const hasCalendars = providerCalendars.length > 0;
            const isGoogle = provider.type === 'gmail';

            return (
              <Card key={provider.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 flex items-center justify-center rounded-lg ${isGoogle ? 'bg-red-100' : 'bg-blue-100'}`}>
                        {isGoogle
                          ? <Calendar className="h-5 w-5 text-red-600" />
                          : <Mail className="h-5 w-5 text-blue-600" />
                        }
                      </div>
                      <div>
                        <h3 className="font-medium">{provider.email}</h3>
                        <p className="text-xs text-muted-foreground">
                          {isGoogle ? 'Google' : 'Outlook'}
                          {provider.last_calendar_sync_at
                            ? ` · Synced ${formatDistanceToNow(new Date(provider.last_calendar_sync_at), { addSuffix: true })}`
                            : hasCalendars ? ' · Not synced yet' : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {hasCalendars ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSync(provider.id)}
                          disabled={syncCalendar.isPending}
                        >
                          {syncCalendar.isPending && syncCalendar.variables === provider.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                          )}
                          Sync
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={isGoogle ? handleGoogleConnect : handleOutlookConnect}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Enable Calendar
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDisconnectClick(provider)}
                        disabled={checkingProviderId === provider.id}
                        className="text-destructive h-8 w-8"
                        aria-label={`Disconnect ${provider.email}`}
                      >
                        {checkingProviderId === provider.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Calendar list with toggles */}
                  {hasCalendars && (
                    <div className="border-t mt-4 pt-4">
                      <button
                        onClick={() => toggleExpanded(provider.id)}
                        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {providerCalendars.length} calendar{providerCalendars.length !== 1 ? 's' : ''}
                      </button>
                      {isExpanded && (
                        <div className="mt-3 space-y-3">
                          {providerCalendars.map(cal => (
                            <div key={cal.id} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div
                                  className="h-3 w-3 rounded-full"
                                  style={{ backgroundColor: cal.color || '#6b7280' }}
                                />
                                <div>
                                  <span className="text-sm font-medium">{cal.name}</span>
                                  {cal.is_primary && (
                                    <span className="ml-2 text-xs text-muted-foreground">(Primary)</span>
                                  )}
                                </div>
                              </div>
                              <Switch
                                checked={cal.is_selected}
                                onCheckedChange={() => toggleCalendar.mutate({
                                  calendarId: cal.id,
                                  isSelected: !cal.is_selected,
                                })}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Disconnect confirmation dialog — two variants:
          (a) Hermes campaigns affected → unified SenderDisconnectConfirmDialog
              (lists campaigns + calendar warning together)
          (b) No campaigns affected → original calendar-impact-only dialog. */}
      {disconnectTarget && affectedCampaigns.length > 0 && (
        <SenderDisconnectConfirmDialog
          open={!!disconnectTarget}
          onOpenChange={(open) => {
            if (!open && !isDisconnecting) {
              setDisconnectTarget(null);
              setAffectedCampaigns([]);
            }
          }}
          senderEmail={disconnectTarget.email}
          affectedCampaigns={affectedCampaigns}
          isProcessing={isDisconnecting}
          onConfirm={handleDisconnect}
          extraWarning={
            <p className="text-sm text-muted-foreground">
              This will also remove all synced calendars and events from this account.
              This action cannot be undone.
            </p>
          }
        />
      )}

      {disconnectTarget && affectedCampaigns.length === 0 && (
        <AlertDialog
          open={!!disconnectTarget}
          onOpenChange={(open) => {
            if (!open && !isDisconnecting) {
              setDisconnectTarget(null);
              setAffectedCampaigns([]);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect {disconnectTarget?.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove all synced calendars and events from this account.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDisconnecting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDisconnecting ? "Disconnecting..." : "Disconnect"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
